import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export function nameValidator(allowNumbers: boolean = false, allowSpaces: boolean = false): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value?.trim();

    if (!value) return null;
    if (!allowSpaces && /\s/.test(value)) {
      return { invalidName: 'Spaces are not allowed.' };
    }
    if (allowSpaces && /\s{2,}/.test(value)) {
      return { invalidName: 'Only one space is allowed between words.' };
    }
    if (!allowNumbers && /\d/.test(value)) {
      return { invalidName: 'Numbers are not allowed.' };
    }
    const allowedPattern = allowNumbers ? /^[a-zA-Z0-9_'\-\s]*$/ : /^[a-zA-Z_'\-\s]*$/;
    if (!allowedPattern.test(value)) {
      return {
        invalidName: 'Special characters are not allowed. Only underscores (_), apostrophes (\'), and hyphens (-) are allowed.'
      };
    }

    return null;
  };
}

// Mirror of iDenfy's accepted firstName/lastName character set (probed from the
// live API): all Unicode letters + space - ' ’ . & \ ` (leading/trailing/double
// separators allowed), no digits, no other symbols, and at least one letter.
// Any name that passes this is guaranteed to pass iDenfy KYC token generation.
// Use ONLY for first_name / last_name / name — NOT for display_name.
const IDENFY_NAME_PATTERN = /^[\p{L}\p{M} '’.&`\\-]+$/u;
const IDENFY_NAME_HAS_LETTER = /\p{L}/u;

export function idenfyNameValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value?.trim();

    if (!value) return null;
    if (/\d/.test(value)) {
      return { invalidName: 'Numbers are not allowed.' };
    }
    if (!IDENFY_NAME_PATTERN.test(value) || !IDENFY_NAME_HAS_LETTER.test(value)) {
      return {
        invalidName: 'Only letters, spaces and - ’ . & \\ ` are allowed.'
      };
    }

    return null;
  };
}

/**
 * Upper bound for a single first/last name on registration paths that collect the
 * name themselves (Apple sign-up's name-completion modal). Frontend-only: the
 * backend's isValidPersonName has no length limit.
 */
export const PERSON_NAME_MAX_LENGTH = 100;

/**
 * Canonical form of a user-supplied name: non-strings become '', surrounding
 * whitespace is trimmed, and internal runs of whitespace collapse to one space.
 */
export function normalizePersonName(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim().replace(/\s+/g, ' ');
}

/**
 * True when a name is acceptable for a new registration after normalization: the
 * iDenfy-mirror charset plus PERSON_NAME_MAX_LENGTH. The backend checks the same
 * charset but not the length.
 */
export function isValidRegistrationName(value: unknown): boolean {
  const name = normalizePersonName(value);
  return name.length > 0
    && name.length <= PERSON_NAME_MAX_LENGTH
    && IDENFY_NAME_PATTERN.test(name)
    && IDENFY_NAME_HAS_LETTER.test(name);
}

/**
 * Form validator for a required registration name. Reports `required` for empty or
 * whitespace-only input, `maxlength` past PERSON_NAME_MAX_LENGTH, and otherwise
 * defers to idenfyNameValidator's `invalidName` messages.
 */
export function registrationNameValidator(): ValidatorFn {
  const charsetValidator = idenfyNameValidator();
  return (control: AbstractControl): ValidationErrors | null => {
    const name = normalizePersonName(control.value);
    if (!name) return { required: true };
    if (name.length > PERSON_NAME_MAX_LENGTH) {
      return { maxlength: { requiredLength: PERSON_NAME_MAX_LENGTH, actualLength: name.length } };
    }
    return charsetValidator({ value: name } as AbstractControl);
  };
}
